import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles'
import type React from 'react'
import { greyPalette } from './grey.palette'

const primaryColor = '#09293E'
const secondaryColor = '#f44336'
const textPrimaryColor = '#231f20'
const whiteColor = '#ffffff'
const errorColor = '#fa0040'
const greyColor = '#9b9b9b'
const successColor = '#2ecc71'
const infoColor = '#4bd5f5'
const warningColor = '#ff8200'
const backdropColor = 'rgba(9, 41, 62, 0.8)'

export const getPixelInREM = (pixelSize: number): string => {
    return `${pixelSize / 16}rem`
}

export const greyTextFieldStyles = (theme: Theme) => ({
    textField: {
        borderBottom: `1px solid ${greyColor}`,
        fontWeight: 500,
        fontSize: getPixelInREM(16),
        color: textPrimaryColor
    },
    input: {
        '&:before': {
            borderBottom: 'none'
        },
        '&:after': {
            borderBottom: 'none'
        }
    }
})

export const getResponsiveWidth = (
    theme: Theme,
    desktop: string,
    tablet: string,
    mobile: string
) => ({
    [theme.breakpoints.down('sm')]: {
        width: mobile
    },
    [theme.breakpoints.between('sm', 'md')]: {
        width: tablet
    },
    [theme.breakpoints.up('md')]: {
        width: desktop
    }
})

declare module '@mui/material/styles' {
    interface Theme {
        custom: {
            headerHeight: number
            colors: {
                oxfordBlue: React.CSSProperties['color']
                chartreuseGreen: React.CSSProperties['color']
                darkGrey: React.CSSProperties['color']
                backgrounds: {
                    hard: React.CSSProperties['color']
                    soft: React.CSSProperties['color']
                }
                white: React.CSSProperties['color']
                red: React.CSSProperties['color']
                blueGrayLight: React.CSSProperties['color']
                lightGrayBackground: React.CSSProperties['color']
            }
        }
    }
    interface ThemeOptions {
        custom?: {
            headerHeight?: number
            colors?: {
                oxfordBlue: React.CSSProperties['color']
                chartreuseGreen: React.CSSProperties['color']
                darkGrey: React.CSSProperties['color']
                backgrounds: {
                    hard: React.CSSProperties['color']
                    soft: React.CSSProperties['color']
                }
                white: React.CSSProperties['color']
                red: React.CSSProperties['color']
                blueGrayLight: React.CSSProperties['color']
                lightGrayBackground: React.CSSProperties['color']
            }
        }
    }
}

const createCustomTheme = (options: ThemeOptions) => {
    return createTheme({
        ...options
    })
}

const materialTheme = createCustomTheme({
    typography: {
        fontFamily: 'Roboto, Barlow, Helvetica, Arial, sans-serif',
        h1: { fontWeight: 700 },
        h2: { fontWeight: 700 },
        h3: { fontWeight: 700 },
        h4: { fontWeight: 700 },
        h5: { fontWeight: 700 },
        h6: { fontWeight: 700 },
        subtitle1: { fontWeight: 600 },
        subtitle2: { fontWeight: 600 },
        button: { fontWeight: 600 }
    },
    palette: {
        primary: {
            main: primaryColor
        },
        secondary: {
            main: secondaryColor
        },
        error: {
            main: errorColor
        },
        success: {
            main: successColor
        },
        info: {
            main: infoColor
        },
        warning: {
            main: warningColor
        },
        text: {
            primary: textPrimaryColor
        },
        grey: greyPalette
    },
    components: {
        MuiTableCell: {
            styleOverrides: {
                head: {
                    backgroundColor: greyPalette[50],
                    color: greyPalette[600],
                    fontWeight: 500
                },
                body: { fontWeight: 400 }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '& td:nth-of-type(2), & td:last-of-type': {
                        fontWeight: 600
                    }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    height: getPixelInREM(40),
                    borderRadius: getPixelInREM(6),
                    lineHeight: getPixelInREM(17),
                    '&:hover': {
                        backgroundColor: 'transparent'
                    }
                },
                startIcon: {
                    '& svg': {
                        width: getPixelInREM(14),
                        height: getPixelInREM(14)
                    }
                },
                outlined: {
                    border: `${getPixelInREM(2)} solid ${primaryColor}`,
                    '&:hover': {
                        border: `${getPixelInREM(2)} solid ${primaryColor}`,
                        backgroundColor: 'transparent'
                    },
                    padding: `${getPixelInREM(11)} ${getPixelInREM(15)} ${getPixelInREM(12)}`
                },
                outlinedPrimary: {
                    border: `${getPixelInREM(2)} solid ${primaryColor}`,
                    '&:hover': {
                        border: `${getPixelInREM(2)} solid ${primaryColor}`
                    }
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: primaryColor,
                        boxShadow: 'none'
                    },
                    padding: `${getPixelInREM(11)} ${getPixelInREM(15)} ${getPixelInREM(12)}`
                },
                containedPrimary: {
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: primaryColor,
                        boxShadow: 'none'
                    },
                    '&:disabled': {
                        backgroundColor: primaryColor,
                        color: 'white',
                        opacity: 0.7,
                        pointerEvents: 'none'
                    }
                },
                containedSecondary: {
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: secondaryColor,
                        boxShadow: 'none'
                    },
                    '&:disabled': {
                        backgroundColor: secondaryColor,
                        color: 'white',
                        opacity: 0.7,
                        pointerEvents: 'none'
                    }
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: 'transparent'
                    }
                }
            },
            defaultProps: {
                disableRipple: true
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: getPixelInREM(6),
                    color: 'white',
                    fontWeight: 600,
                    backgroundColor: greyPalette[600]
                }
            }
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: backdropColor
                }
            }
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true
            }
        }
    },
    custom: {
        headerHeight: 60,
        colors: {
            oxfordBlue: '#09293e',
            chartreuseGreen: '#baf700',
            darkGrey: '#7f7f7f',
            backgrounds: {
                hard: '#E3E8E8',
                soft: '#FAF7F0'
            },
            white: whiteColor,
            red: secondaryColor,
            blueGrayLight: '#ebeff0',
            lightGrayBackground: 'rgb(245, 247, 248)'
        }
    }
})

export default materialTheme
